import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Profil } from '../shared/profil';
import { Observable, throwError } from 'rxjs';
import { retry, catchError, map } from 'rxjs/operators';
import {Product} from '../shared/product';
import {ProductNames} from '../shared/productNames';
import {Column} from '../shared/Column';
import {ColumnChosen} from '../shared/ColumnChosen';
import {ConfigData} from '../shared/ConfigData';
import {Wholeseller} from '../shared/Wholeseller';
import {AuthService} from '../core/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  user: Profil;
  addParams: string;

  // Define API
  apiURL = 'https://office.next77.pl/RestApi';
  apiURL_2 = 'http://localhost:3000/';
  // apiURL = 'http://localhost/RestApi';

  constructor(private http: HttpClient, private authService: AuthService) {
    this.user = this.authService.getLoggedUser();
  }

  /*========================================
    CRUD Methods for consuming RESTful API
  =========================================*/

  // Http Options
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };
  private addParam: string;

  // CONFIG - nowy backend (/config-data) zamiast RestApi/configData/read.php (PHP zwracalo 500).
  // Zamiast surowego "where" przyjmuje typ slownika z whitelisty backendu (currency, gtu, language,
  // orderTab, paymentForm, transport), opcjonalny jezyk i pole sortowania (value | label).
  getConfigData(params: { type: string; language?: string; order?: 'value' | 'label' }): Observable<ConfigData[]> {
    let httpParams = new HttpParams().set('type', params.type);
    if (params.language) {
      httpParams = httpParams.set('language', params.language);
    }
    if (params.order) {
      httpParams = httpParams.set('order', params.order);
    }
    return this.http.get<ConfigData[]>('/config-data', { params: httpParams })
      .pipe(
        retry(1),
        catchError(this.handleError)
      );
  }

  // Nowy backend (/profil) zamiast Profils/read.php - patrz office/backend/src/profil.
  // Odpowiedz {list,count} rozpakowana do golej tablicy, bo tak od zawsze
  // konsumuja to wszyscy wywolujacy (ProfilData, filtry, urlopy...).
  getProfils( params: any = {} ): Observable<Profil[]> {
    this.addParams = '';
    if ( Object.keys(params).length > 0 ){
      this.addParams = '?';
      for (const key in params) {
        if ( this.addParams !== '?' ){
          this.addParams = this.addParams + '&';
        }
        this.addParams = this.addParams + key + '=' + params[key];
      }
    }
    return this.http.get<{ list: Profil[]; count: number }>('/profil' + this.addParams)
      .pipe(
        retry(1),
        map(res => res.list),
        catchError(this.handleError)
      );
  }
  // Produkty
  // Nowy backend (/product) zamiast Produkt/read.php. Zwraca juz {list,count},
  // dokladnie tak jak konsumenci tego wywolania od zawsze zakladali (mimo ze
  // stary typ TS klamal ze to gola tablica) - bez rozpakowywania.
  getProdukty( params ): Observable<{ list: Product[]; count: number }> {
    this.addParams = '';
    if ( Object.keys(params).length > 0 ){
      this.addParams = '?';
      for (const key in params) {
        if ( this.addParams !== '?' ){
          this.addParams = this.addParams + '&';
        }
        this.addParams = this.addParams + key + '=' + params[key];
      }
    }
    return this.http.get<{ list: Product[]; count: number }>(
      '/product' + this.addParams
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  // Nowy backend (/product-text) zamiast ProduktNazwy/read.php. Odpakowana do
  // golej tablicy - konsumenci robia res.length/res[i] wprost.
  getProduktyNazwy(params): Observable<ProductNames[]> {
    this.addParams = '';
    if ( Object.keys(params).length > 0 ){
      this.addParams = '?';
      for (const key in params) {
        if ( this.addParams !== '?' ){
          this.addParams = this.addParams + '&';
        }
        this.addParams = this.addParams + key + '=' + params[key];
      }
    }
    return this.http.get<{ list: ProductNames[] }>(
      '/product-text' + this.addParams
    ).pipe(
      retry(1),
      map(res => res.list),
      catchError(this.handleError)
    );
  }

  // COLUMNS
  // Nowy backend (/columns) zamiast Columns/*.php - legacy PHP stracilo
  // uwierzytelnianie po przejsciu logowania na JWT (2026-08-05, zapis kolumn
  // konczyl sie bledem "[object Object]"). Stare wywolania przekazuja rozne
  // kombinacje (placer wprost albo w where, user wprost albo w where) -
  // wyciagamy oba tutaj, zeby nie dotykac kilkunastu konsumentow.
  private columnsParam(params: any, name: string): string {
    if (params && params[name] !== undefined && params[name] !== null) {
      return String(params[name]);
    }
    const where = String(params?.where ?? '');
    const m = name === 'user'
      ? where.match(/users*=s*(d+)/)
      : where.match(/placers*=s*"([^"]+)"/);
    return m ? m[1] : '';
  }

  getColumns( params ): Observable<[Column]> {
    const placer = this.columnsParam(params, 'placer');
    return this.http.get<[Column]>(
      '/columns?placer=' + encodeURIComponent(placer)
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }
  getColumnsChosen( params ): Observable<[ColumnChosen]> {
    const placer = this.columnsParam(params, 'placer');
    const user = this.columnsParam(params, 'user');
    return this.http.get<[ColumnChosen]>(
      '/columns/chosen?user=' + encodeURIComponent(user) + '&placer=' + encodeURIComponent(placer)
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }
  updateColumnsChosen( lista ): Observable<any> {
    return this.http.put<any>('/columns/chosen', lista, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Wholesellers
  // Nowy backend (/wholeseller) zamiast Wholesellers/read.php. Zwraca juz
  // {list,count} - bez rozpakowywania, tak jak konsumenci od zawsze zakladali.
  getWholesellers( params ): Observable<{ list: Wholeseller[]; count: number }> {
    this.addParams = '';
    if ( Object.keys(params).length > 0 ){
      this.addParams = '?';
      for (const key in params) {
        if ( this.addParams !== '?' ){
          this.addParams = this.addParams + '&';
        }
        this.addParams = this.addParams + key + '=' + params[key];
      }
    }
    return this.http.get<{ list: Wholeseller[]; count: number }>(
      '/wholeseller' + this.addParams
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  // HELPERS
  checkAddWhere( addWhere, colName, colVal ) {
    if ( colVal !== undefined ) {
      if ( addWhere.length === 0 ) {
        addWhere = '?';
      } else {
        addWhere = addWhere + '&';
      }
      addWhere = addWhere + colName + '=' + colVal;
    }
    return addWhere;
  }

  // ERROR HANDLER
  handleError(error) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Get client-side error
      errorMessage = error.error.message;
    } else {
      // Get server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    window.alert(errorMessage);
    return throwError(errorMessage);
  }
}
